import axios from "axios";

import myConfig from "../../configs/config";

function authHeaders() {
  return {
    Authorization: `Bearer ${localStorage.getItem("client-token")}`,
  };
}

export async function fetchFleetMeterSummary(farmId, { dateFrom, dateTo, presets } = {}) {
  const params = { farm_id: farmId };
  if (dateFrom && dateTo) {
    params.date_from = dateFrom;
    params.date_to = dateTo;
  } else {
    params.presets = presets || "day,week,month";
  }
  const response = await axios.get(
    `${myConfig.API_URL}/mobile/field-data/fleet-meter-summary/`,
    {
      params,
      headers: authHeaders(),
    }
  );
  return response.data;
}

function normalizeMachineIdent(value) {
  return String(value || "")
    .trim()
    .toLocaleLowerCase("pt-BR");
}

export function mergeFleetPresetRows(presets) {
  const rows = [];
  const byEquipmentId = new Map();
  const byIdent = new Map();

  function registerRow(row) {
    if (row.equipmentId != null && row.equipmentId !== "") {
      byEquipmentId.set(String(row.equipmentId), row);
    }
    const ident = normalizeMachineIdent(row.machineIdent);
    if (ident) {
      byIdent.set(ident, row);
    }
  }

  function resolveRow(machine) {
    const equipmentId =
      machine.equipmentId != null && machine.equipmentId !== ""
        ? String(machine.equipmentId)
        : null;
    const ident = normalizeMachineIdent(machine.machineIdent);

    if (equipmentId && byEquipmentId.has(equipmentId)) {
      return byEquipmentId.get(equipmentId);
    }
    if (ident && byIdent.has(ident)) {
      return byIdent.get(ident);
    }

    const row = {
      ...machine,
      periods: {},
    };
    rows.push(row);
    registerRow(row);
    return row;
  }

  ["day", "week", "month"].forEach((preset) => {
    const machines = (presets && presets[preset] && presets[preset].machines) || [];
    machines.forEach((machine) => {
      const row = resolveRow(machine);
      row.periods[preset] = {
        workedHours: machine.workedHours,
        workedKm: machine.workedKm,
        sessionCount: machine.sessionCount,
      };
      row.equipmentId =
        machine.equipmentId != null ? machine.equipmentId : row.equipmentId;
      row.machineIdent = machine.machineIdent || row.machineIdent;
      row.meterType = machine.meterType || row.meterType;
      row.currentHourMeter =
        machine.currentHourMeter != null
          ? machine.currentHourMeter
          : row.currentHourMeter;
      row.currentHourMeterAt =
        machine.currentHourMeterAt != null
          ? machine.currentHourMeterAt
          : row.currentHourMeterAt;
      row.currentOdometerKm =
        machine.currentOdometerKm != null
          ? machine.currentOdometerKm
          : row.currentOdometerKm;
      row.currentOdometerAt =
        machine.currentOdometerAt != null
          ? machine.currentOdometerAt
          : row.currentOdometerAt;
      row.currentHourMeterLat =
        machine.currentHourMeterLat != null
          ? machine.currentHourMeterLat
          : row.currentHourMeterLat;
      row.currentHourMeterLng =
        machine.currentHourMeterLng != null
          ? machine.currentHourMeterLng
          : row.currentHourMeterLng;
      row.currentOdometerLat =
        machine.currentOdometerLat != null
          ? machine.currentOdometerLat
          : row.currentOdometerLat;
      row.currentOdometerLng =
        machine.currentOdometerLng != null
          ? machine.currentOdometerLng
          : row.currentOdometerLng;
      row.currentHourMeterOperatorId =
        machine.currentHourMeterOperatorId != null
          ? machine.currentHourMeterOperatorId
          : row.currentHourMeterOperatorId;
      row.currentHourMeterSessionUuid =
        machine.currentHourMeterSessionUuid != null
          ? machine.currentHourMeterSessionUuid
          : row.currentHourMeterSessionUuid;
      row.currentOdometerOperatorId =
        machine.currentOdometerOperatorId != null
          ? machine.currentOdometerOperatorId
          : row.currentOdometerOperatorId;
      row.currentOdometerSessionUuid =
        machine.currentOdometerSessionUuid != null
          ? machine.currentOdometerSessionUuid
          : row.currentOdometerSessionUuid;
      registerRow(row);
    });
  });

  return rows;
}

export function filterFleetRowsByMeterTab(rows, meterTab) {
  const list = Array.isArray(rows) ? rows : [];
  if (meterTab === "both") {
    return list;
  }
  if (meterTab === "odometer") {
    return list.filter(
      (row) => row.meterType === "odometer" || row.meterType === "both"
    );
  }
  return list.filter(
    (row) =>
      row.meterType === "hour_meter" ||
      row.meterType === "both" ||
      !row.meterType
  );
}

function maintenanceUsesKm(item) {
  return item && item.meter_type === "km";
}

function resolveMeterTab(item, meterTab) {
  if (meterTab === "both") {
    return maintenanceUsesKm(item) ? "odometer" : "hour_meter";
  }
  return meterTab;
}

function maintenanceMatchesMeterTab(item, meterTab) {
  if (!item) return false;
  if (meterTab === "both") return true;
  return meterTab === "odometer" ? maintenanceUsesKm(item) : !maintenanceUsesKm(item);
}

function maintenanceRemainingValue(item, row, meterTab) {
  if (!item) return null;
  const resolvedTab = resolveMeterTab(item, meterTab);
  if (resolvedTab === "odometer") {
    const target = Number(item.next_due_km);
    const current = Number(row.currentOdometerKm);
    if (!Number.isFinite(target) || !Number.isFinite(current)) return null;
    if (current >= target) {
      return { overdue: true, value: current - target };
    }
    return { overdue: false, value: target - current };
  }

  const target = Number(item.targetHourMeterDisplay);
  const current = Number(
    item.currentHourMeterDisplay != null
      ? item.currentHourMeterDisplay
      : row.currentHourMeter
  );
  if (!Number.isFinite(target) || !Number.isFinite(current)) return null;
  if (item.isOverdue || current >= target) {
    return { overdue: true, value: current - target };
  }
  return { overdue: false, value: target - current };
}

function maintenanceSortScore(item, row, meterTab) {
  const remaining = maintenanceRemainingValue(item, row, meterTab);
  if (!remaining) return Number.POSITIVE_INFINITY;
  if (remaining.overdue) {
    return -remaining.value;
  }
  return remaining.value;
}

export function pickNextMaintenanceForEquipment(maintenances, equipmentId, row, meterTab) {
  const candidates = (maintenances || []).filter(
    (item) =>
      Number(item.equipmentId || item.equipment) === Number(equipmentId) &&
      maintenanceMatchesMeterTab(item, meterTab)
  );
  if (!candidates.length) return null;

  return candidates.sort((left, right) => {
    const leftScore = maintenanceSortScore(left, row, meterTab);
    const rightScore = maintenanceSortScore(right, row, meterTab);
    if (leftScore !== rightScore) {
      return leftScore - rightScore;
    }
    return String(left.name || "").localeCompare(String(right.name || ""), "pt-BR");
  })[0];
}

export function buildMaintenanceByEquipmentMap(maintenances, rows, meterTab) {
  const map = new Map();
  (rows || []).forEach((row) => {
    const equipmentId = row.equipmentId;
    if (!equipmentId) return;
    const next = pickNextMaintenanceForEquipment(maintenances, equipmentId, row, meterTab);
    if (next) {
      map.set(String(equipmentId), next);
    }
  });
  return map;
}

export function getMaintenanceRemaining(item, row, meterTab) {
  return maintenanceRemainingValue(item, row, meterTab);
}

export function getMaintenanceTargetValue(item, meterTab) {
  if (!item) return null;
  if (resolveMeterTab(item, meterTab) === "odometer") {
    return item.next_due_km;
  }
  return item.targetHourMeterDisplay;
}

export function maintenanceMeterSuffix(item, meterTab) {
  return resolveMeterTab(item, meterTab) === "odometer" ? "km" : "h";
}

export function maintenanceDetailPath(farmId, equipmentId, maintenanceId) {
  return `/${farmId}/equipment/machine/${equipmentId}/appointments/detail/${maintenanceId}`;
}

function parsePositiveMetric(value) {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return 0;
  return num;
}

function fleetMaintenanceSortScore(row, meterTab, maintenanceByEquipment) {
  if (!maintenanceByEquipment) {
    return { group: 2, overdueBy: 0, remaining: Number.POSITIVE_INFINITY };
  }

  const maintenance = maintenanceByEquipment.get(String(row.equipmentId));
  if (!maintenance) {
    return { group: 2, overdueBy: 0, remaining: Number.POSITIVE_INFINITY };
  }

  const remaining = maintenanceRemainingValue(maintenance, row, meterTab);
  if (!remaining || !Number.isFinite(remaining.value)) {
    return { group: 2, overdueBy: 0, remaining: Number.POSITIVE_INFINITY };
  }

  if (remaining.overdue) {
    return { group: 0, overdueBy: remaining.value, remaining: 0 };
  }

  return { group: 1, overdueBy: 0, remaining: remaining.value };
}

function fleetRowSortScore(row, meterTab, sortPreset) {
  const period = row.periods && row.periods[sortPreset];
  const hours = parsePositiveMetric(period && period.workedHours);
  const km = parsePositiveMetric(period && period.workedKm);
  const metric =
    meterTab === "odometer" ? km : meterTab === "both" ? hours || km : hours;
  const sessions = parsePositiveMetric(period && period.sessionCount);

  if (metric > 0) {
    return { tier: 0, metric, sessions };
  }
  if (sessions > 0) {
    return { tier: 1, metric, sessions };
  }
  return { tier: 2, metric, sessions };
}

export function sortFleetRows(rows, meterTab, sortPreset, options = {}) {
  const { maintenanceByEquipment } = options;

  return [...rows].sort((left, right) => {
    const leftMaintenance = fleetMaintenanceSortScore(
      left,
      meterTab,
      maintenanceByEquipment
    );
    const rightMaintenance = fleetMaintenanceSortScore(
      right,
      meterTab,
      maintenanceByEquipment
    );

    if (leftMaintenance.group !== rightMaintenance.group) {
      return leftMaintenance.group - rightMaintenance.group;
    }

    if (leftMaintenance.group === 0) {
      if (rightMaintenance.overdueBy !== leftMaintenance.overdueBy) {
        return rightMaintenance.overdueBy - leftMaintenance.overdueBy;
      }
    } else if (leftMaintenance.group === 1) {
      if (rightMaintenance.remaining !== leftMaintenance.remaining) {
        return leftMaintenance.remaining - rightMaintenance.remaining;
      }
    }

    const leftScore = fleetRowSortScore(left, meterTab, sortPreset);
    const rightScore = fleetRowSortScore(right, meterTab, sortPreset);

    if (leftScore.tier !== rightScore.tier) {
      return leftScore.tier - rightScore.tier;
    }

    if (leftScore.tier === 0 && rightScore.metric !== leftScore.metric) {
      return rightScore.metric - leftScore.metric;
    }

    if (leftScore.tier === 1 && rightScore.sessions !== leftScore.sessions) {
      return rightScore.sessions - leftScore.sessions;
    }

    return String(left.machineIdent || "").localeCompare(
      String(right.machineIdent || ""),
      "pt-BR"
    );
  });
}
